import { createContext, useContext, useReducer, type ReactNode } from 'react';

interface LoginModalState {
  showLoginDialog: boolean;
}

type LoginModalAction =
  | { type: 'OPEN_LOGIN_DIALOG' }
  | { type: 'CLOSE_LOGIN_DIALOG' };

interface LoginModalContextType {
  state: LoginModalState;
  openLoginDialog: () => void;
  closeLoginDialog: () => void;
}

const initialState: LoginModalState = {
  showLoginDialog: false,
};

const loginModalReducer = (
  state: LoginModalState,
  action: LoginModalAction,
): LoginModalState => {
  switch (action.type) {
    case 'OPEN_LOGIN_DIALOG':
      return {
        ...state,
        showLoginDialog: true,
      };
    case 'CLOSE_LOGIN_DIALOG':
      return {
        ...state,
        showLoginDialog: false,
      };
    default:
      return state;
  }
};

const LoginModalContext = createContext<LoginModalContextType | undefined>(
  undefined,
);

interface LoginModalProviderProps {
  children: ReactNode;
}

export const LoginModalProvider = ({ children }: LoginModalProviderProps) => {
  const [state, dispatch] = useReducer(loginModalReducer, initialState);

  const openLoginDialog = () => {
    dispatch({ type: 'OPEN_LOGIN_DIALOG' });
  };

  const closeLoginDialog = () => {
    dispatch({ type: 'CLOSE_LOGIN_DIALOG' });
  };

  const value: LoginModalContextType = {
    state,
    openLoginDialog,
    closeLoginDialog,
  };

  return (
    <LoginModalContext.Provider value={value}>
      {children}
    </LoginModalContext.Provider>
  );
};

export const useLoginModal = (): LoginModalContextType => {
  const context = useContext(LoginModalContext);

  if (context === undefined) {
    throw new Error('useLoginModal must be used within a LoginModalProvider');
  }

  return context;
};
